/**
 * Turn a string into a number, 
 * removes string formatting to get the correct JS number.
 * For example when the number uses . as thousands separator while JS considers that the decimal separator.
 * 
 * @param {String} str ie "123.456,12"
 * @returns {Number} ie 123456.12
 */
const toNumber = (str) => {
    str = str.replaceAll('.','');
    str = str.replaceAll(',','.');
    return Number(str);
}

/**
 * Turn a number into a string with thousands separators
 * and decimal separator (if applicable).
 * 
 * @param {Number} number ie 123456.12
 * @returns {String} ie "123.456,12"
 */
const toThousandsString = (number) => {
    return number.toLocaleString('nl-NL')
}

/**
 * Count from start number to end number using an interval,
 * to create a number that is visually count up/down.
 * Interval speed is determined by countDuration,
 * so that all numbers count for the same duration.
 * 
 * @param {HTMLElement} $element element that shows the counting
 * @param {Number} start number to count from
 * @param {Number} end number to count to
 * @returns 
 */
const countLengthInS = 2;
const countLengthInMs = countLengthInS * 1000;
const countFromTo = ($element, start, end) => {
    if (start === end) return;

    const directionIsUp = start < end;

    const difference = directionIsUp ? end - start : start - end;

    const loops = Math.floor(Math.random() * 100) + 80; // returns a random integer from 80 to 100
    const step = difference / loops;
    const timeout = countLengthInMs / loops;

    let curval = start;
    $element.innerHTML = curval;

    let interval = window.setInterval(() => {
        if ((directionIsUp && curval + step >= end) || (!directionIsUp && curval - step <= end)) {
            window.clearInterval(interval);
            $element.innerHTML = toThousandsString(end);
        } else {
            if (directionIsUp) {
                curval += step;
            } else {
                curval -= step;
            }
            $element.innerHTML = toThousandsString(Math.round(curval));
        }
    }, timeout);
    return interval;
}

/**
 * Set wrapper ready for script,
 * ie remove class that hides elements in case of no script.
 * 
 * @param {HTMLElement} $wrapper wrapper element
 */
const setReadyForScript = ($wrapper) => {
    $wrapper.querySelectorAll('.faf-requires-script').forEach($el => $el.classList.remove('faf-requires-script'));
}

/**
 * Create and return SVG element with optional default settings.
 *
 * @param {Object} settings 
 * @returns 
 */
const createSVGElement = (settings) => {
    const $element = document.createElementNS('http://www.w3.org/2000/svg', settings.shape);
    if (settings.x) $element.setAttribute('x', settings.x);
    if (settings.y) $element.setAttribute('y', settings.y);
    if (settings.cx) $element.setAttribute('cx', settings.cx);
    if (settings.cy) $element.setAttribute('cy', settings.cy);
    if (settings.r) $element.setAttribute('r', settings.r);
    if (settings.stroke) $element.setAttribute('stroke', settings.stroke);
    if (settings.strokeWidth) $element.setAttribute('stroke-width', settings.strokeWidth);
    if (settings.fill) $element.setAttribute('fill', settings.fill);
    if (settings.tabindex) $element.setAttribute('tabindex', settings.tabindex);
    if (settings.classes) $element.classList.add(...settings.classes);
    return $element;
};

/**
 * Init Facts-And-Figures type 1.
 * 
 * @param {HTMLElement} $wrapper wrapper element
 */
const initFaf1 = ($wrapper) => {
    let percentageInterval, valueInterval;

    // Data
    let settings;
    try {
        settings = JSON.parse($wrapper.getAttribute('data-faf-settings'));
    } catch (e) {
        console.warn(e);
    }
    if (!settings || !settings.items || !settings.range) return;

    const values = settings.items;
    // values also available in different formats for convenience
    const keys = Object.keys(values);
    const valuesAsArray = Object.values(values);

    if (keys.length === 0) return;

    let defaultValueIndex = valuesAsArray.findIndex(obj => obj.default);
    if (defaultValueIndex === -1) defaultValueIndex = 0;
    const defaultValue = valuesAsArray[defaultValueIndex];

    if (!defaultValue) return;

    // Elements in general
    const $svg = $wrapper.querySelector('.faf-svg');
    const $range = $wrapper.querySelector('.faf-range input');
    const $caption = $wrapper.querySelector('.faf-figcaption');

    if (!$svg || !$range || !$caption) return;

    // Elements that will receive values from data
    const $value = $wrapper.querySelector('.js-faf-1-value');
    const $description = $wrapper.querySelector('.js-faf-1-description');
    const $percentage = $wrapper.querySelector('.js-faf-1-percentage');

    if (!$value || !$description || !$percentage) return;

    // 1. Extend $range based on data
    $range.setAttribute('min', settings.range.min);
    $range.setAttribute('max', settings.range.max);
    $range.setAttribute('step', settings.range.step);
    $range.value = defaultValue.range;

    // 2. Generate $svg elements based on data
    // 2.1 Define svg elements and attributes as objects to pass to createSVGElement
    const svg = {
        backdrop: {
            shape: 'circle', cx: 278.744, cy: 278.558, fill: "#E2E2E2", stroke: "#CECECE", classes: ['faf-1-selectable'], tabindex: '0'
        },
        centerdot: {
            shape: 'circle', cx: 278.744, cy: 278.558, r: 6, fill: "#E40521"
        },
        group: {
            shape: 'g', classes: ['faf-1-group']
        },
        rings: [
            { shape: 'circle', cx: 278.674, cy: 278.83, r: 277.329, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.83, r: 268.366, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.829, r: 259.404, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.83, r: 250.442, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.829, r: 241.48, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.83, r: 232.518, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.83, r: 223.555, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.829, r: 214.593, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.83, r: 205.631, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 196.669, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 187.706, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 178.744, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 169.782, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 160.82, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 151.858, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 142.895, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 133.933, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 124.971, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 116.009, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 107.047, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 98.0843, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.674, cy: 278.342, r: 89.1221, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 80.1599, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 71.1977, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 62.2355, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 53.2733, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 44.3111, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 35.3489, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 26.3866, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 17.4244, stroke: "rgba(0,0,0,0.1)" },
            { shape: 'circle', cx: 278.744, cy: 278.985, r: 8.46221, stroke: "rgba(0,0,0,0.1)" }
        ]
    };

    // 2.2 Render svg elements
    // Divide the rings by the datasets. Remainder of the division are grouped with the last chunk in the loop
    const chunkSize = Math.floor(svg.rings.length / valuesAsArray.length);
    // For each dataset, render the backdrop and the rings
    for (let i = 0; i < valuesAsArray.length; i += 1) {
        const group = [];
        // If it's the last item, use all remaining rings (in case of remainder of a division)
        if (i === valuesAsArray.length - 1) {
            group.push(...svg.rings.splice(0))
        } else {
            group.push(...svg.rings.splice(0, chunkSize));
        }
        // Every group is preceded by a backdrop
        const $backdrop = createSVGElement(svg.backdrop);
        $backdrop.classList.add('faf-1-selectable-' + (i + 1))
        $backdrop.setAttribute('data-value', keys[i]);
        $backdrop.setAttribute('r', group[0].r);
        $svg.appendChild($backdrop);
        // Create group that holds rings
        const $group = createSVGElement(svg.group);
        $group.classList.add('faf-1-group-' + keys[i]);
        group.forEach((ring, index) => {
            // If it's the first item, item must have fill
            if (index === 0) ring.fill = 'white';
            $group.appendChild(createSVGElement(ring));
        })
        $svg.appendChild($group);
    }
    // Finally add the centerdot
    $svg.appendChild(createSVGElement(svg.centerdot));
    
    // 3. Interaction
    const $selectables = $wrapper.querySelectorAll('.faf-1-selectable');

    /**
     * Value changed, update elements.
     * 
     * @param {Object} data value object
     * @param {String} key key of value in values object
     */
    const onValueChange = (data, key) => {
        // Update description
        $description.innerHTML = data.description; // as innerHTML because of possible markup in value

        // Update percentage
        clearInterval(percentageInterval);
        percentageInterval = countFromTo($percentage, toNumber($percentage.textContent), toNumber(data.percentage));

        // Update value
        clearInterval(valueInterval);
        valueInterval = countFromTo($value, toNumber($value.textContent), toNumber(data.range));

        // Update active group
        $svg.querySelectorAll('.faf-1-group.is-active').forEach($el => $el.classList.remove('is-active'));
        $svg.querySelector('.faf-1-group.faf-1-group-' + key).classList.add('is-active');

        // Move caption anywhere between 50% and 75% to roughly the same position as the active group
        const index = keys.indexOf(key);
        const fraction = 1 - (index / keys.length);
        $caption.style.top = `${50 + (25 * fraction)}%`;
    }

    /**
     * User interacted with SVG.
     * 
     * @param {Object} data value object
     * @param {String} key key of value in values object
     */
    const onSelect = (data, key) => {
        // Update $range since value changed through interaction with svg instead of interaction with range
        $range.value = data.range;
        onValueChange(data, key)
    }
    $selectables.forEach($selectable => {
        const key = $selectable.getAttribute('data-value');
        const data = values[key];

        $selectable.addEventListener('click', () => {
            onSelect(data, key);
        }) 

        $selectable.addEventListener('keyup', (e) => {
            // Element has a tabindex and user can trigger interaction through 'ENTER' in addition to 'click'
            if(e.keyCode === 13) {
                onSelect(data, key);
            }
       });
    });

    /**
     * User interacted with range.
     * 
     * @param {Object} data value object
     * @param {String} key key of value in values object
     */
    const onRangeChange = () => {
        const range = $range.value;

        const index = valuesAsArray.findIndex(obj => obj.range == range);
        onValueChange(valuesAsArray[index], keys[index])
    }
    $range.addEventListener('input', onRangeChange);

    // 4. Hydrate with initial value
    onSelect(valuesAsArray[defaultValueIndex], keys[defaultValueIndex]);
    setReadyForScript($wrapper);
}

/**
 * Init Facts-And-Figures type 2.
 * 
 * @param {HTMLElement} $wrapper wrapper element
 */
const initFaf2 = ($wrapper) => {
    let numeratorInterval;

    // Data
    let settings;
    try {
        settings = JSON.parse($wrapper.getAttribute('data-faf-settings'));
    } catch (e) {
        console.warn(e);
    }
    if (!settings || !settings.items || !settings.denominator) return;

    const values = settings.items;
    // values also available in different formats for convenience
    const keys = Object.keys(values);
    const valuesAsArray = Object.values(values);

    if (keys.length === 0) return;

    let defaultValueIndex = valuesAsArray.findIndex(obj => obj.default);
    if (defaultValueIndex === -1) defaultValueIndex = 0;
    const defaultValue = valuesAsArray[defaultValueIndex];

    if (!defaultValue) return;

    // Elements in general
    const $select = $wrapper.querySelector('.faf-select select');
    const $grid = $wrapper.querySelector('.faf-2-grid');
    const $template = $wrapper.querySelector('#faf-2-grid-item-template');

    if (!$select || !$grid || !$template) return;

    // Elements that will receive values from data
    const $numerator = $wrapper.querySelector('.js-faf-2-numerator');
    const $denominator = $wrapper.querySelector('.js-faf-2-denominator');
    const $description = $wrapper.querySelector('.js-faf-2-description');

    if (!$numerator || !$denominator || !$description) return;

    // 1. Extend $select based on data
    for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        const data = values[key];
        const $option = document.createElement('option');
        if (data.default) $option.selected = true;
        $option.setAttribute('data-value', key);
        $option.textContent = data.label;
        $select.appendChild($option);
    }

    // 2. Generate $grid items based on data
    for (let i = 0; i < settings.denominator; i += 1) {
        $grid.appendChild($template.content.cloneNode(true));
    }

    // 3. Animate denominator
    countFromTo($denominator, toNumber($denominator.textContent), settings.denominator);

    // 4. Interaction
    const $items = $wrapper.querySelectorAll('.faf-2-grid-item');

    /**
     * User interacted with $select.
     */
    const onSelectChange = () => {
        const value = $select.selectedOptions[0];
        if (!value) return;

        const key = value.getAttribute('data-value');
        const data = values[key];

        const numerator = toNumber(data.numerator);
        const description = data.description;

        // Animate numerator
        clearInterval(numeratorInterval);
        numeratorInterval = countFromTo($numerator, toNumber($numerator.textContent), numerator);

        // Update description
        $description.textContent = description;
        const num = toNumber(data.numerator);

        // Update active items
        $items.forEach(($item, index) => {
            $item.classList.toggle('is-active', index < num);
        });
    }
    $select.addEventListener('change', onSelectChange);

    // 5. Hydrate with initial value (through selected attribute on option)
    onSelectChange();
    setReadyForScript($wrapper);
}

/**
 * Init Facts-And-Figures type 3.
 * 
 * @param {HTMLElement} $wrapper wrapper element
 */
const initFaf3 = ($wrapper) => {
    let valueInterval;

    // Data
    let settings;
    try {
        settings = JSON.parse($wrapper.getAttribute('data-faf-settings'));
    } catch (e) {
        console.warn(e);
    }
    if (!settings || !settings.items || !settings.x || !settings.y) return;

    const values = settings.items;
    // values also available in different formats for convenience
    const keys = Object.keys(values);
    const valuesAsArray = Object.values(values);

    if (keys.length === 0) return;

    let defaultValueIndex = valuesAsArray.findIndex(obj => obj.default);
    if (defaultValueIndex === -1) defaultValueIndex = 0;
    const defaultValue = valuesAsArray[defaultValueIndex];

    if (!defaultValue) return;

    // Elements in general
    const $svg = $wrapper.querySelector('.faf-svg');

    if (!$svg) return;

    // Elements that will receive values from data
    const $value = $wrapper.querySelector('.js-faf-3-value');

    if (!$value) return;

    // 1. Generate $svg elements based on data
    // 1.1 Define svg elements and attributes as objects to pass to createSVGElement
    const svg = { 
        seriespath: { shape: 'path', stroke: "#E40521" },
        seriesfill: { shape: 'path', fill: "white" },
        lineandpoint: { shape: 'g', classes: ['line-and-point'], tabindex: '0' },
        lineandpointhitbox: { shape: 'path', stroke: "transparent", strokeWidth: '15' },
        lineforpoint: { shape: 'path', stroke: "black", strokeWidth: '1.25', classes: ['line-for-point'] },
        point: { shape: 'circle', r: '6', fill: '#E40521', classes: ['point'] },
        axisX: { shape: 'path', stroke: 'black', strokeWidth: '1.2' },
        gridX: { shape: 'path', stroke: '#ECECEC', strokeWidth: '1.2' },
    };

    // 1.2 Define settings for graph
    const graph = {
        left: 9.82928, // Horizontal starting point of space available for graph
        right: 513.78, // Horizontal end point of space available for graph
        top: 0.110535, // Vertical start point of space available for graph
        bottom: 695.111 // Vertical end point of space available for graph
    }
    graph.width = graph.right - graph.left; // Width available for graph
    graph.height = graph.bottom - graph.top; // Height available for graph
    graph.xwidth = graph.width / (settings.x.length - 1); // Width inbetween horizontal data points
    graph.yheight = graph.height / (settings.y.length - 1); // Width inbetween vertical data points
    graph.ymin = toNumber(settings.y[0]); // Value that corresponds with 0% / lower vertical data point
    graph.ymax = toNumber(settings.y[settings.y.length - 1]); // Value that corresponds with 100% / highest vertical data point

    // 1.3 Collect settings for series (point and line)
    let series = [];
    for (let i = 0; i < settings.x.length; i += 1) {
        const text = settings.x[i];
        const x = graph.left + (graph.xwidth * i);
        const value = toNumber(valuesAsArray[i].value);
        const fraction = (value - graph.ymin) / (graph.ymax - graph.ymin);
        const y = graph.top + (graph.height * (1 - fraction));
        series.push({
            key: keys[i],
            point: {
                x, y, value
            },
            line: {
                text,
                start: {
                    x: x,
                    y: graph.top,
                },
                end: {
                    x: x,
                    y: graph.bottom,
                }
            }
        })
    }

    // 1.4 Create and collect $points elements (point and line) and collect series path
    let d = ''; // Start series path
    const $points = [];
    for (let i = 0; i < settings.x.length; i += 1) {

         // If first index, start with "M" (Move To), else continue with "L" (Line To)
        const command = i === 0 ? 'M' : 'L';
        const serie = series[i];
        d += `${command}${serie.point.x} ${serie.point.y}`;

        // Group that combines line and point
        const $lineandpoint = createSVGElement(svg.lineandpoint);
        $lineandpoint.setAttribute('data-value', serie.key);

        // Additional path for larger hitbox
        const $lineandpointhitbox = createSVGElement(svg.lineandpointhitbox);
        $lineandpointhitbox.setAttribute('d', `M ${serie.line.start.x} ${serie.line.start.y} L ${serie.line.end.x} ${serie.line.end.y}`);

        // Line
        const $lineforpoint = createSVGElement(svg.lineforpoint);
        $lineforpoint.setAttribute('d', `M ${serie.line.start.x} ${serie.line.start.y} L ${serie.line.end.x} ${serie.line.end.y}`);

        // Point
        const $point  = createSVGElement(svg.point);
        $point.setAttribute('cx', serie.point.x);
        $point.setAttribute('cy', serie.point.y);

        // Add elements in correct order
        $lineandpoint.appendChild($lineandpointhitbox);
        $lineandpoint.appendChild($lineforpoint);
        $lineandpoint.appendChild($point);
        $points.push($lineandpoint);
    }

    // Create and set series path
    const $path = createSVGElement(svg.seriespath);
    $path.setAttribute('d', d);

    // Draw series fill (using series path)
    const $fill = createSVGElement(svg.seriesfill);
    d += `L ${graph.right} ${graph.bottom}`; // Line to bottom right
    d += `L ${graph.left} ${graph.bottom}`; // Line to bottom left
    d += 'Z'; // Line to start of path
    $fill.setAttribute('d', d);
    $svg.appendChild($fill);

    // Draw x axis
    const $x = createSVGElement(svg.axisX);
    $x.setAttribute('d', `M ${graph.left} ${graph.bottom} H ${graph.right}`);
    $svg.appendChild($x);

    // Draw x axis gridlines
    for (let i = 1; i < settings.y.length - 1; i += 1) { // skips first and last, so start at 1 and end at length - 1
        const $xgrid = createSVGElement(svg.gridX);
        $xgrid.setAttribute('d', `M ${graph.left} ${graph.bottom - ((graph.yheight * i))} H ${graph.right}`);
        $svg.appendChild($xgrid);
    }

    // Draw x axis labels
    for (let i = 0; i < settings.x.length; i += 1) {
        const serie = series[i];
        const $xlabel = createSVGElement({ shape: 'text', x: serie.line.end.x, y: serie.line.end.y + 44 });
        $xlabel.setAttribute('text-anchor', 'middle');
        $xlabel.textContent = serie.line.text;
        $svg.appendChild($xlabel);
    }

    // Add points and path to svg
    $points.forEach($p => $svg.appendChild($p));
    $svg.appendChild($path);

    // 2. Interaction

    /**
     * User interacted with $points
     * @param {HTMLElement} $point interacted element
     * @param {Object} data value object
     */
    const onInteract = ($point, data) => {
        // Update active point
        $points.forEach($p => $p.classList.remove('is-active'));
        $point.classList.add('is-active');
        // Update value
        clearInterval(valueInterval);
        valueInterval = countFromTo($value, toNumber($value.textContent), toNumber(data.value));
    }
    $points.forEach($point => {
        const key = $point.getAttribute('data-value');
        const data = values[key];

        $point.addEventListener('click', () => {
            onInteract($point, data);
        }) 

        $point.addEventListener('keyup', (e) => {
            // Element has a tabindex and user can trigger interaction through 'ENTER' in addition to 'click'
            if(e.keyCode === 13) {
                onInteract($point, data);
            }
       })
    })

    // 3. Hydrate with initial value
    onInteract($points[defaultValueIndex], defaultValue);
    setReadyForScript($wrapper);
}

const init = () => {
    document.querySelectorAll('.js-faf-1').forEach($wrapper => initFaf1($wrapper));
    document.querySelectorAll('.js-faf-2').forEach($wrapper => initFaf2($wrapper));
    document.querySelectorAll('.js-faf-3').forEach($wrapper => initFaf3($wrapper));
}

module.exports = init;
